import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import SpreadsheetEditor, { toEditorRows, fromEditorRows } from './components/SpreadsheetEditor';
import ImportModal from './components/ImportModal';
import BacktestChart from './components/BacktestChart';
import { parseAmount } from './lib/colors';
import {
  isSupabaseReady,
  listBacktestSheets,
  createBacktestSheet,
  getBacktestSheet,
  renameBacktestSheet,
  deleteBacktestSheet,
  getBacktestRows,
  saveBacktestRows,
} from './lib/supabase';

const DEFAULT_AMOUNT = 10000; // $ per position if no amount specified

function SheetPicker({ onSelect, onNew }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listBacktestSheets().then(setSheets).finally(() => setLoading(false));
  }, []);

  return (
    <div className="stonks-sheet-list-wrap">
      <div className="stonks-sheet-list-title">Backtests</div>
      {!isSupabaseReady() && (
        <div className="stonks-db-notice">
          ⚠️ Supabase not configured for this environment — set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in Netlify production env vars, then redeploy.
        </div>
      )}
      <button className="btn-stonks btn-stonks-primary" onClick={onNew} style={{ width: 'fit-content' }}>
        ＋ New Backtest
      </button>
      {loading && <div className="stonks-loading" style={{ flex: 'none', marginTop: 20 }}><div className="stonks-spinner" /></div>}
      {!loading && sheets.length === 0 && (
        <div className="stonks-empty" style={{ flex: 'none', marginTop: 20 }}>
          <div>No saved backtests yet.</div>
        </div>
      )}
      {sheets.map(sheet => (
        <div key={sheet.id} className="stonks-sheet-list-item" onClick={() => onSelect(sheet.id)}>
          <div className="stonks-sheet-list-name">📈 {sheet.name}</div>
          <div className="stonks-sheet-list-date">
            {new Date(sheet.updated_at).toLocaleDateString()}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Portfolio calculation ─────────────────────────────────────────────────────
// Given holdings and historical price data, compute portfolio value over time
function computePortfolio(holdings, historical) {
  // holdings: [{ ticker, purchaseDate, amountUSD }]
  // historical: { TICKER: [{ date, close }] }
  // Returns: [{ date, value, positions: { TICKER: value } }]

  // Gather all dates across all tickers
  const allDates = new Set();
  Object.values(historical).forEach(history => {
    history.forEach(d => allDates.add(d.date));
  });
  const sortedDates = [...allDates].sort();
  if (!sortedDates.length) return [];

  // Price lookup map: { TICKER: { 'YYYY-MM-DD': close } }
  const priceMap = {};
  Object.entries(historical).forEach(([ticker, history]) => {
    priceMap[ticker] = {};
    history.forEach(d => { priceMap[ticker][d.date] = d.close; });
  });

  // Fill forward: for each ticker, last known price
  function getPriceOnDate(ticker, date, dateIdx, sortedDates) {
    if (priceMap[ticker]?.[date]) return priceMap[ticker][date];
    // Look backwards
    for (let i = dateIdx - 1; i >= 0; i--) {
      const d = sortedDates[i];
      if (priceMap[ticker]?.[d]) return priceMap[ticker][d];
    }
    return null;
  }

  // For each holding, compute shares purchased
  const sharesMap = {}; // { TICKER: total shares }
  holdings.forEach(h => {
    if (!priceMap[h.ticker]) return;
    // Find the price on or after purchase date
    const purchaseDate = h.purchaseDate || sortedDates[0];
    let purchasePrice = null;
    for (const d of sortedDates) {
      if (d >= purchaseDate && priceMap[h.ticker]?.[d]) {
        purchasePrice = priceMap[h.ticker][d];
        break;
      }
    }
    if (!purchasePrice) return;
    const shares = h.amountUSD / purchasePrice;
    sharesMap[h.ticker] = (sharesMap[h.ticker] || 0) + shares;
  });

  // Compute portfolio value for each date
  return sortedDates.map((date, i) => {
    const positions = {};
    let total = 0;

    holdings.forEach(h => {
      const ticker = h.ticker;
      const purchaseDate = h.purchaseDate || sortedDates[0];
      if (date < purchaseDate) return; // not yet purchased

      const shares = sharesMap[ticker];
      if (!shares) return;

      const price = getPriceOnDate(ticker, date, i, sortedDates);
      if (!price) return;

      const val = shares * price;
      positions[ticker] = (positions[ticker] || 0) + val;
      total += val;
    });

    return { date, value: total, positions };
  }).filter(d => d.value > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Backtesting() {
  const { sheetId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const view      = searchParams.get('view')  || 'edit';
  const btStart   = searchParams.get('start') || '';
  const btEnd     = searchParams.get('end')   || new Date().toISOString().split('T')[0];

  const [sheetName, setSheetName] = useState('New Backtest');
  const [rows, setRows]           = useState([{ id: 1, ticker: '', type: '', date: '', amount: '' }]);
  const [saving, setSaving]       = useState(false);
  const [running, setRunning]     = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [historical, setHistorical]     = useState(null);
  const [btError, setBtError]     = useState(null);

  const setParam = useCallback((key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Load sheet from DB
  useEffect(() => {
    if (!sheetId) return;
    setLoadingSheet(true);
    Promise.all([getBacktestSheet(sheetId), getBacktestRows(sheetId)])
      .then(([sheet, dbRows]) => {
        if (sheet?.name) setSheetName(sheet.name);
        setRows(toEditorRows(dbRows));
      })
      .finally(() => setLoadingSheet(false));
  }, [sheetId]);

  const handleNew = useCallback(async () => {
    const sheet = await createBacktestSheet('New Backtest');
    navigate(sheet ? `/stonks/backtesting/${sheet.id}` : '/stonks/backtesting/local');
  }, [navigate]);

  const handleSelect = useCallback((id) => navigate(`/stonks/backtesting/${id}`), [navigate]);

  const handleRename = useCallback(async (name) => {
    setSheetName(name);
    if (sheetId && sheetId !== 'local') await renameBacktestSheet(sheetId, name);
  }, [sheetId]);

  const handleDelete = useCallback(async () => {
    if (!sheetId || sheetId === 'local') { navigate('/stonks/backtesting'); return; }
    if (!window.confirm('Delete this backtest?')) return;
    await deleteBacktestSheet(sheetId);
    navigate('/stonks/backtesting');
  }, [sheetId, navigate]);

  const handleRunBacktest = useCallback(async () => {
    const validRows = fromEditorRows(rows);
    if (!validRows.length) { alert('Add at least one ticker.'); return; }

    setSaving(true);
    if (sheetId && sheetId !== 'local') await saveBacktestRows(sheetId, validRows);
    setSaving(false);

    setRunning(true);
    setBtError(null);

    const tickers = [...new Set(validRows.map(r => r.ticker))];
    // Determine backtest period
    const earliestDate = validRows
      .filter(r => r.date)
      .map(r => r.date)
      .sort()[0] || (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().split('T')[0]; })();

    const startDate = btStart || earliestDate;
    const endDate   = btEnd   || new Date().toISOString().split('T')[0];

    const url = `/api/stonksHistorical?tickers=${encodeURIComponent(tickers.join(','))}&start=${startDate}&end=${endDate}`;
    try {
      const data = await fetch(url).then(r => r.json());
      if (data.historical) {
        setHistorical(data.historical);
        setParam('view', 'visualize');
      } else {
        setBtError(data.error || 'No historical data returned');
      }
    } catch (e) {
      setBtError(e.message);
    } finally {
      setRunning(false);
    }
  }, [rows, sheetId, btStart, btEnd, setParam]);

  // Build portfolio data
  const portfolioData = useMemo(() => {
    if (!historical) return [];
    const validRows = fromEditorRows(rows);
    const holdings = validRows.map(r => ({
      ticker: r.ticker,
      purchaseDate: r.date || null,
      amountUSD: parseAmount(r.amount) || DEFAULT_AMOUNT,
    }));
    return computePortfolio(holdings, historical);
  }, [historical, rows]);

  // ── No sheetId: show picker ──────────────────────────────────────────────
  if (!sheetId) return <SheetPicker onSelect={handleSelect} onNew={handleNew} />;

  if (loadingSheet) return <div className="stonks-loading"><div className="stonks-spinner" /><span>Loading…</span></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Ribbon ───────────────────────────────────────────────── */}
      <div className="stonks-sheet-ribbon">
        <input
          className="stonks-sheet-name-input"
          value={sheetName}
          onChange={e => setSheetName(e.target.value)}
          onBlur={e => handleRename(e.target.value)}
        />

        {view === 'edit' && (
          <>
            <button className="btn-stonks btn-stonks-secondary" onClick={() => setShowImport(true)}>
              📥 Import
            </button>
            <button
              className="btn-stonks btn-stonks-green"
              onClick={handleRunBacktest}
              disabled={saving || running}
            >
              {running ? '⏳ Running…' : saving ? '💾 Saving…' : '▶ Run Backtest'}
            </button>
          </>
        )}

        {view === 'visualize' && (
          <button className="btn-stonks btn-stonks-secondary" onClick={() => setParam('view', 'edit')}>
            ✏️ Edit Positions
          </button>
        )}

        <div className="stonks-sheet-ribbon-right">
          <button className="btn-stonks btn-stonks-ghost" onClick={() => navigate('/stonks/backtesting')}>
            ← All Backtests
          </button>
          <button className="btn-stonks btn-stonks-danger" onClick={handleDelete}>🗑 Delete</button>
        </div>
      </div>

      {/* ── Period selector (edit mode) ───────────────────────────── */}
      {view === 'edit' && (
        <div className="stonks-filterbar">
          <span className="stonks-filterbar-label">Backtest Period</span>
          <input
            className="stonks-input"
            type="date"
            value={btStart}
            onChange={e => setParam('start', e.target.value)}
            style={{ fontFamily: 'var(--s-mono)', fontSize: 11, colorScheme: 'dark' }}
          />
          <span style={{ color: 'var(--s-text-muted)', fontSize: 11 }}>→</span>
          <input
            className="stonks-input"
            type="date"
            value={btEnd}
            onChange={e => setParam('end', e.target.value)}
            style={{ fontFamily: 'var(--s-mono)', fontSize: 11, colorScheme: 'dark' }}
          />
          <span style={{ color: 'var(--s-text-muted)', fontSize: 11, marginLeft: 4 }}>
            (leave start blank to use earliest purchase date)
          </span>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--s-text-muted)' }}>
            Default $10,000 USD/position if no amount set
          </div>
        </div>
      )}

      {/* ── Main content ─────────────────────────────────────────── */}
      {view === 'edit' ? (
        <>
          {btError && (
            <div style={{ padding: '8px 14px', color: 'var(--s-red)', fontSize: 12, background: 'var(--s-red-bg)', borderBottom: '1px solid var(--s-border)' }}>
              ⚠️ {btError}
            </div>
          )}
          <SpreadsheetEditor rows={rows} onChange={setRows} />
        </>
      ) : (
        <BacktestChart
          portfolioData={portfolioData}
          holdings={fromEditorRows(rows)}
        />
      )}

      {showImport && (
        <ImportModal
          onImport={r => setRows(toEditorRows(r))}
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  );
}
